import React from 'react';
import { Check, X } from 'lucide-react';
import SeedIcon from '../../../assets/seed-white-icon.svg';
import ModelTrainingIcon from '../../../assets/model-training-white-icon.svg';
import HandPlantIcon from '../../../assets/hand-plant.svg';
import RewardsGrowthImage from '../../../assets/welcoming-slide-5-rewards-growth.png';
import { Divider, Img, useBreakpointValue } from '@chakra-ui/react';

export default function Slide5() {
  const dividerOrientation =
    useBreakpointValue<'horizontal' | 'vertical'>({
      base: 'horizontal',
      md: 'vertical'
    }) ?? 'horizontal';
  return (
    <div className="animate-in slide-in-from-right duration-300">
      {/* Heading */}
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center whitespace-nowrap">
        How Twygs Rewards Growth
      </h1>

      {/* Top Section: Graphic + Pathway Explanation */}
      <div className="flex flex-col md:flex-row md:items-center gap-10 mb-10">
        {/* Left Graphic (interconnected growth cycles) */}
        <div className="mx-auto md:mx-0 relative w-[260px] h-[260px] hidden md:block">
          <img
            src={RewardsGrowthImage}
            alt="Rewards Growth"
            className="w-full h-full object-contain"
          />
        </div>

        {/* Right Pathway Text */}
        <div className="flex-1">
          <div className="flex flex-col gap-3">
            <div className="mb-2">
              <div className="flex items-center gap-2 bg-growth-green-100 rounded-full px-2 py-1 w-fit text-sm font-semibold text-growth-green">
                <Img src={HandPlantIcon} className="w-5 h-5" alt="Hand Plant" />
                <span className="text-base">Contribute → Earn → Grow</span>
              </div>
              <p className="ml-10 mt-1 text-sm">
                Engage with the platform.. Community members award you with Twigs, which grow into
                Seeds. Your capital to invest in people, ideas, and earn real returns.
              </p>
            </div>
            <div className="mb-2">
              <div className="flex items-center gap-2 bg-growth-green-900 rounded-full px-2 py-1 w-fit text-sm font-semibold text-white">
                <Img src={SeedIcon} alt="Seed" className="w-5 h-5" />
                <span className="text-base">Engage → Learn → Build Value</span>
              </div>
              <p className="ml-10 mt-1 text-sm">
                Read, respond, and invest with intention. Each interaction strengthens ideas worth
                spreading and helps you earn alongside them.
              </p>
            </div>
            <div className="mb-2">
              <div className="flex items-center gap-2 bg-growth-green rounded-full px-2 py-1 w-fit text-sm font-semibold text-white">
                <Img src={ModelTrainingIcon} alt="Model Training" className="w-5 h-5" />
                <span className="text-base">Invest → Amplify → Return</span>
              </div>
              <p className="ml-10 mt-1 text-sm">
                Support content you believe in. As it gains visibility, your returns grow too.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Card: Thrives vs Fades */}
      <div className="mt-2 mx-0 md:mx-14">
        <div className="bg-gradient-to-r from-[#F9F6FE] to-[#F4FDF6] rounded-xl border border-[#F3E8FF] p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Thrives */}
            <div className="flex-1">
              <h3 className="text-green-700 font-extrabold mb-4 flex items-center gap-2">
                What Thrives Here
              </h3>
              <ul className="space-y-3">
                {[
                  'Insight and context',
                  'Respectful exchange',
                  'Original thinking that teaches or connects'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-800">
                    <Check className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Divider: horizontal on mobile, vertical on md+ */}
            <Divider
              orientation={dividerOrientation}
              borderColor="#F3E8FF"
              borderWidth="1px"
              alignSelf="stretch"
              height={{ md: 'auto' }}
            />
            {/* Fades */}
            <div className="flex-1">
              <h3 className="text-red-700 font-extrabold mb-4 flex items-center gap-2">
                What Fades Away
              </h3>
              <ul className="space-y-3">
                {['Bait and outrage', 'Empty repetition', 'Noise without contribution'].map(
                  (item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-800">
                      <X className="w-4 h-4 text-red-500 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
